/**
 * Chasse commune : le tableau partage, toutes les retouches, et la course pour
 * les reperer. Sert aux modes `hunt` et `battle`, qui ne different que par
 * l'elimination de fin de manche.
 *
 * Coquille React mince autour de HuntEngine, comme GuessBoard l'est autour de
 * GuessEngine. Deux differences avec la devinette ordinaire : on cherche
 * plusieurs retouches a la fois, et on cherche jusqu'a la fin du chrono au lieu
 * d'un unique essai.
 *
 * Le meme composant sert pendant la revelation, `frozen` : rien ne bouge, mais
 * le tableau et ses retouches restent a l'ecran, ce qui est tout l'interet du
 * mode. On voit enfin ou tout le monde s'etait cache.
 */
import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { huntPlacements, type HuntTarget, type RoomSnapshot } from '@retusch/shared';
import { HuntEngine, type HuntSlot } from './HuntEngine.js';
import { loadArtwork } from './artwork.js';
import { useT } from '../locales/useT.js';
import { playerById } from '../store/roomStore.js';
import { Avatar } from '../ui/Avatar.js';
import { PhaseTimer } from '../ui/PhaseTimer.js';
import { observeSize } from './canvas/observeSize.js';

/** Aller-retour automatique du curseur a l'ouverture de la revelation. */
const AUTO_SLIDE_MS = 1100;
/** Demi-largeur de la zone de prise du curseur, en px. */
const SPLIT_GRAB_PX = 16;

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export interface HuntBoardProps {
  readonly room: RoomSnapshot;
  readonly file: string;
  readonly targets: readonly HuntTarget[];
  readonly drawings: Readonly<Record<number, ArrayBuffer>>;
  readonly found: Readonly<Record<number, { drawerId: string; finderId: string }>>;
  /**
   * Slots dans l'ordre de DECOUVERTE. Vide hors battle royale : les autres
   * modes n'ont rien a classer, ils comptent des points.
   */
  readonly foundOrder?: readonly number[];
  /** Identifiant de CE joueur : sa propre retouche se signale a part. */
  readonly selfId: string | null;
  /** Phase de revelation : plus rien a chercher, on regarde. */
  readonly frozen: boolean;
  /**
   * Ce joueur REGARDE la chasse sans y prendre part (spectateur, ou public du
   * mode stream). A distinguer du verrouillage : lui n'a pas rate, il n'a
   * jamais eu la main.
   */
  readonly watching?: boolean;
  readonly guessTimeMs: number;
  readonly onStrike: (x: number, y: number) => void;
}

export function HuntBoard({
  room,
  file,
  targets,
  drawings,
  found,
  foundOrder,
  selfId,
  frozen,
  watching = false,
  guessTimeMs,
  onStrike,
}: HuntBoardProps): ReactElement {
  const t = useT();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const engineRef = useRef<HuntEngine | null>(null);
  const onStrikeRef = useRef(onStrike);
  onStrikeRef.current = onStrike;
  const handleRef = useRef<HTMLDivElement | null>(null);
  const self = room.players.find((player) => player.id === selfId) ?? null;
  /**
   * Verrouille : un rate met fin a la chasse pour ce joueur. `done` vient du
   * serveur, qui fait autorite (et couvre la reconnexion en pleine manche) ;
   * `missed` est l'echo local, pour que le plateau se fige sans attendre
   * l'aller-retour.
   */
  const [missed, setMissed] = useState(false);
  /**
   * Le streamer cherche SANS LIMITE : il est le seul chercheur de sa manche, et
   * son premier rate fermait la phase pour toute la table. Le serveur ne le
   * verrouille plus ; il fallait aussi retirer le verrou LOCAL, sinon l'ecran
   * annoncait « vous ne cherchez plus cette manche » sur une partie que le
   * serveur laissait pourtant continuer.
   */
  const unlimited = self?.streamer === true;
  const locked =
    frozen ||
    watching ||
    self?.eliminated === true ||
    (!unlimited && (self?.done === true || missed));
  // Un elimine, comme un joueur verrouille, regarde la chasse continuer : il
  // garde le pan et le zoom, mais ses clics ne partent plus.
  const canStrikeRef = useRef(true);
  canStrikeRef.current = !locked;
  // Lu a la CONSTRUCTION du moteur, qui est asynchrone : une valeur capturee
  // au rendu serait celle d'avant le premier instantane.
  const unlimitedRef = useRef(unlimited);
  unlimitedRef.current = unlimited;

  // Nouvelle manche : le verrou local repart a zero.
  const round = targets.length === 0 ? 0 : (targets[0]?.slot ?? 0);
  useEffect(() => {
    setMissed(false);
  }, [file, round]);

  const foundSlots = useMemo(() => Object.keys(found).map(Number), [found]);
  const foundRef = useRef(foundSlots);
  foundRef.current = foundSlots;

  // Les blobs arrivent un par un, juste apres le JSON. Tant qu'il en manque un,
  // sa zone serait blanche : c'est la reponse offerte, donc on attend.
  const complete = targets.every((target) => drawings[target.slot] !== undefined);

  // Le moteur se construit UNE fois par manche : `drawings` change d'identite a
  // chaque blob recu, le reconstruire a chaque fois ferait clignoter la vue et
  // perdre le pan/zoom en cours.
  /**
   * Les pseudos, sous une forme STABLE.
   *
   * `room.players` est un tableau NEUF a chaque instantane, et une trouvaille
   * en declenche un : le moteur se reconstruisait donc a chaque fois que
   * quelqu'un trouvait une retouche, ce qui remettait le cadrage a zero chez
   * TOUT LE MONDE, en pleine chasse. Cette cle ne change que lorsqu'un pseudo
   * change vraiment.
   */
  const labelKey = room.players.map((player) => `${player.id}:${player.nickname}`).join('|');

  const slots = useMemo<readonly HuntSlot[] | null>(() => {
    if (!complete) return null;
    return targets.map((target) => ({
      slot: target.slot,
      drawerId: target.drawerId,
      zone: target.zone,
      palette: target.palette,
      drawing: drawings[target.slot] ?? null,
      own: target.drawerId === selfId,
      // Le nom s'affiche seulement une fois la retouche trouvee : le moteur le
      // detient d'avance, il ne le peint qu'a ce moment-la.
      label: room.players.find((player) => player.id === target.drawerId)?.nickname ?? '',
    }));
    // `drawings` et `room.players` sont deliberement hors des dependances : le
    // premier ne change plus de contenu une fois complet, le second est couvert
    // par `labelKey`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targets, complete, selfId, labelKey]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null || slots === null) return;

    let alive = true;
    let engine: HuntEngine | null = null;

    void loadArtwork(file)
      .then((image) => {
        if (!alive) return;
        engine = new HuntEngine({
          canvas,
          image,
          slots,
          onStrike: (point) => onStrikeRef.current(point.x, point.y),
          canStrike: () => canStrikeRef.current,
          onMiss: () => setMissed(true),
          lockOnMiss: () => !unlimitedRef.current,
          // Le curseur original/retouche n'existe qu'a la revelation : pendant
          // la chasse, il montrerait l'œuvre intacte, c'est-a-dire la reponse.
          split: frozen,
          autoSlideMs: prefersReducedMotion() ? 0 : AUTO_SLIDE_MS,
        });
        engineRef.current = engine;
        // La poignee suit le trait dore. Un transform, pas un `left` : c'est la
        // seule ecriture DOM de la boucle, elle ne doit rien faire refluer.
        engine.onSplit(() => {
          const node = handleRef.current;
          if (node !== null) node.style.transform = `translateX(${engine?.splitPx ?? 0}px)`;
        });
        engine.start();
        // Le tableau charge de facon asynchrone : les trouvailles deja connues
        // (toute la revelation, ou une reconnexion en pleine chasse) sont
        // arrivees avant que le moteur existe. Sans ce rappel, elles restaient
        // invisibles sur le tableau alors qu'elles s'affichaient dans la bande.
        engine.setFound(foundRef.current);
        // Meme raison pour les places : une reconnexion en pleine chasse les
        // connait deja, et rien ne les recalculera ensuite.
        engine.setPlacements(placementsRef.current);
      })
      .catch(() => undefined);

    // La taille du canvas change aussi SANS evenement de fenetre : on observe
    // la boite elle-meme, sinon le rendu garde une geometrie perimee.
    const stopResize = observeSize(canvas, () => engineRef.current?.resize());

    return () => {
      alive = false;
      stopResize();
      engine?.destroy();
      engineRef.current = null;
    };
  }, [file, slots, frozen]);

  // Les trouvailles arrivent en cours de phase : on ne reconstruit pas le
  // moteur pour autant, on lui dit seulement ce qui est desormais verrouille.
  useEffect(() => {
    engineRef.current?.setFound(foundSlots);
  }, [foundSlots]);

  /**
   * Battle royale : la place de chacun, mise a jour a CHAQUE decouverte.
   *
   * Le classement est l'ordre de decouverte a l'envers, et il se lit en direct :
   * une retouche qui tombe annonce tout de suite le rang de son auteur. On
   * traduit les identifiants rendus par `huntPlacements` en slots, seule cle que
   * le moteur connaisse.
   */
  const placements = useMemo(() => {
    if (foundOrder === undefined) return new Map<number, number>();
    const slotOf = new Map(targets.map((target) => [target.drawerId, target.slot]));
    const order = foundOrder
      .map((slot) => targets.find((target) => target.slot === slot)?.drawerId)
      .filter((id): id is string => id !== undefined);
    const places = huntPlacements(order, [...slotOf.keys()]);

    const bySlot = new Map<number, number>();
    for (const [id, rank] of places) {
      const slot = slotOf.get(id);
      if (slot !== undefined) bySlot.set(slot, rank);
    }
    return bySlot;
  }, [foundOrder, targets]);

  const placementsRef = useRef(placements);
  placementsRef.current = placements;

  useEffect(() => {
    engineRef.current?.setPlacements(placements);
  }, [placements]);

  useEffect(() => {
    if (locked) engineRef.current?.lock();
  }, [locked]);

  const remaining = targets.length - foundSlots.length;

  const moveSplit = (event: React.PointerEvent<HTMLDivElement>): void => {
    // Le canvas ne doit surtout pas voir ce geste : il le prendrait pour un pan.
    event.stopPropagation();
    const bounds = event.currentTarget.parentElement?.getBoundingClientRect();
    if (bounds === undefined) return;
    engineRef.current?.setSplit(engineRef.current.splitFromView(event.clientX - bounds.left));
  };

  const onHandleDown = (event: React.PointerEvent<HTMLDivElement>): void => {
    event.currentTarget.setPointerCapture(event.pointerId);
    moveSplit(event);
  };

  const onHandleMove = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (event.buttons === 0 && event.pointerType === 'mouse') return;
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
    moveSplit(event);
  };

  if (slots === null) {
    return <p className="sandbox__status">{t('join.connecting')}</p>;
  }

  return (
    <div className="hunt">
      <div className="hunt__stage">
        <canvas
          ref={canvasRef}
          className={`board__canvas ${frozen ? 'board__canvas--grab' : 'board__canvas--inspect'}`}
        />

        {/* Curseur original / retouche, comme a la revelation classique. Il a sa
            PROPRE poignee : glisser sur le canvas deplace la vue, glisser sur la
            poignee deplace la separation, et les deux gestes ne se marchent
            jamais dessus. */}
        {frozen && (
          <>
            <div
              ref={handleRef}
              className="reveal__split-handle"
              role="separator"
              aria-label={t('reveal.compare')}
              style={{ width: SPLIT_GRAB_PX * 2, marginLeft: -SPLIT_GRAB_PX }}
              onPointerDown={onHandleDown}
              onPointerMove={onHandleMove}
            />
            <div className="reveal__labels reveal__labels--bottom" aria-hidden="true">
              <span>{t('reveal.retouched')}</span>
              <span>{t('reveal.original')}</span>
            </div>
          </>
        )}

        <div className="board__hud">
          <span className="hunt__status" data-tone={missed && !frozen ? 'miss' : undefined}>
            {self?.eliminated === true
              ? t('hunt.eliminated')
              : frozen
                ? t('hunt.over')
                : // « Rate » ne s'adresse qu'a qui a joué son coup. Le dire a
                  // quelqu'un qui n'a jamais eu la main lui reprocherait une
                  // faute qu'il n'a pas commise ; on lui montre le compte des
                  // retouches encore cachees, qui est ce qu'il vient suivre.
                  watching
                  ? t('hunt.remaining', { n: remaining })
                  : locked
                    ? t('hunt.locked')
                    : t('hunt.remaining', { n: remaining })}
          </span>
          <span className="board__timer">
            {/* Le chrono court aussi pendant la revelation : c'est lui qui dit
                combien de temps il reste pour regarder le tableau. */}
            <PhaseTimer endsAt={room.endsAt} durationMs={guessTimeMs} />
          </span>
        </div>
      </div>

      {/* Le tableau de chasse : qui est tombe, et grace a qui. C'est la seule
          facon de savoir ce qui vient de se passer quand tout va vite. */}
      <ul className="hunt__log">
        {targets.map((target) => {
          const kill = found[target.slot];
          const drawer = playerById(room, target.drawerId);
          const finder = kill === undefined ? null : playerById(room, kill.finderId);
          return (
            <li className="hunt__entry" key={target.slot} data-out={kill !== undefined}>
              {drawer !== null && (
                <Avatar
                  playerId={drawer.id} avatar={drawer.avatar}
                  colorIndex={drawer.colorIndex}
                  size={24}
                  dimmed={kill !== undefined}
                />
              )}
              <span className="hunt__who">
                {drawer?.nickname ?? '?'}
                {target.drawerId === selfId && <small> {t('hunt.you')}</small>}
              </span>
              <span className="hunt__fate">
                {kill === undefined
                  ? t('hunt.hidden')
                  : t('hunt.foundBy', { name: finder?.nickname ?? '?' })}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
